import React, { FunctionComponent, useMemo, useState } from "react";
import styles from "./styles.module.scss";
import { Grid, ListItemIcon, Menu, MenuItem, Skeleton } from "@mui/material";
import { strings } from "../../../../localization/LocalizedStrings";
import { ModifyTagDialog } from "../ModifyTagDialog";
import { AttributeDesktop } from "../../AttributeDesktop";
import { toast } from "react-toastify";
import { useContextMenu } from "../../../../hooks/contextMenu";
import { attributeIconMap } from "../../../../models/AttributeUtilities";
import { AttributeType } from "../../AttributeList";
import { ITag, useGetTags } from "../../../../hooks/tags";
import { TagCard } from "../TagCard";
import { Icon, IconType } from "../../../../icons";
import { useResettableDialog } from "../../../../hooks/resettableDialog/useResettableDialog";

export const TagsDesktop: FunctionComponent<ITagsDesktopProps> = ({
  handleClose,
  isPublic,
  columnWidth,
  preselectedIds,
  onTagSelected,
  onClearValue
}) => {
  const [loading, setLoading] = useState(true);
  const [tags, resetTags, setTags, noMoreTags] = useGetTags(isPublic, setLoading);
  const {
    state: modifyTagDialogState,
    key: modifyTagDialogKey,
    openAdd,
    openEdit,
    close: closeModifyTagDialog
  } = useResettableDialog<ITag>();
  const [searchText, setSearchText] = useState("");
  const [contextTag, setContextTag] = useState<ITag>();
  const { menuProps, handleContextMenu, handleMenuClose } = useContextMenu();
  const filteredTags = useMemo(() => {
    if (searchText == "") return tags;
    return tags?.filter(tag => {
      return (
        tag.name.toLowerCase().includes(searchText.toLowerCase()) ||
        tag.description?.toLowerCase().includes(searchText.toLowerCase())
      );
    });
  }, [tags, searchText]);
  return (
    <AttributeDesktop
      handleClose={handleClose}
      isPublic={isPublic}
      items={filteredTags}
      emptyIconType={attributeIconMap[AttributeType.TAGS]}
      emptyString={isPublic ? strings.empty_tags_public : strings.empty_tags}
      addItemString={strings.add_attribute}
      title={strings.tags_title}
      loading={loading}
      setLoading={setLoading}
      onSearchRequested={searchText => setSearchText(searchText)}
      addAction={openAdd}
      refreshAction={resetTags}
      onClearValue={onClearValue}
    >
      {filteredTags?.map(tag => (
        <Grid item key={tag.uuid} xs={columnWidth}>
          <TagCard
            tag={tag}
            onClick={() => {
              if (onTagSelected) {
                onTagSelected(tag);
              } else {
                openEdit(tag);
              }
            }}
            onContextMenu={event => {
              setContextTag(tag);
              handleContextMenu(event);
            }}
            selected={preselectedIds?.includes(tag.uuid)}
          />
        </Grid>
      ))}
      {(loading || !noMoreTags) &&
        Array.from({ length: 20 }, (_value, index) => (
          <Grid key={index} item xs={columnWidth}>
            <Skeleton variant={"rectangular"} sx={{ height: 60 }} className={styles.skeleton} />
          </Grid>
        ))}
      <Menu {...menuProps}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            openEdit(contextTag);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.EditSquare} />
          </ListItemIcon>
          {strings.alert_modify}
        </MenuItem>
      </Menu>
      <ModifyTagDialog
        key={modifyTagDialogKey}
        open={modifyTagDialogState.mode !== "closed"}
        onClose={closeModifyTagDialog}
        tag={modifyTagDialogState.item}
        onNewItem={newTag => {
          const newTags = [...tags, newTag];
          newTags.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { ignorePunctuation: true })
          );
          setTags(newTags);
          toast.success(strings.generic_update_success);
        }}
        onModifiedItem={newTag => {
          const newTags = tags.map(tag => (tag.uuid == newTag.uuid ? newTag : tag));
          newTags.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { ignorePunctuation: true })
          );
          setTags(newTags);
          toast.success(strings.generic_update_success);
        }}
        onDeletedItem={deletedTag => {
          const newTags = tags.filter(tag => tag.uuid != deletedTag.uuid);
          setTags(newTags);
          toast.success(strings.generic_update_success);
        }}
      />
    </AttributeDesktop>
  );
};

export interface ITagsDesktopProps {
  handleClose?: () => void;
  isPublic: boolean;
  columnWidth: number;
  preselectedIds?: string[];
  onTagSelected?: (tag: ITag) => void;
  onClearValue?: () => void;
}
